import { useRef, useState } from 'react';
import { api, ApiClientError } from '../lib/api';
import { Button } from '../atoms';
import type { FormValues } from './Form';
import { ParcelDuplicateWarning } from '../pages/ParcelsList';

/**
 * ParcelDocumentIntake — read a batch of assessor documents into PROPOSED parcel
 * accounts for a human to confirm one at a time.
 *
 * WHY THIS IS NOT THE STAGED-IMPORT PIPELINE. A document names an account; it cannot
 * name which property that account belongs to or which assessor issued it, and both
 * `parcels.property_id` and `parcels.assessor_id` are NOT NULL. Verified against a real
 * Tarrant Appraisal District notice: the reading was perfect — every figure correct — and
 * staging still failed with
 *   ["Missing required field: property_id", "Missing required field: assessor_id"]
 * The setup wizard is the one place those two are already known, which is why the intake
 * lives here and creates through the wizard's own POST /api/parcels rather than through
 * `commitImport`.
 *
 * That choice is also what makes the per-row confirmation REAL rather than a policy.
 * `commitImport` selects `WHERE review_status = 'pending'` and the commit endpoint is
 * called with an empty body, so committing a batch is a blanket accept-all of every
 * parsed row — there is no per-row gate in that pipeline to honour. Here each proposed
 * account is its own request, sent only for a row a person ticked, so "an account is
 * never created without an explicit step" holds structurally: there is no accept-all
 * path to bypass.
 *
 * The figures the document carries are deliberately NOT applied here. Creating the
 * account is the confirmed act; the money is restaged afterwards (zero further model
 * cost, see reuseReading in services/document-extract) and lands in the ordinary review
 * where money belongs.
 */

/** How many documents one intake accepts. Matches the bulk-upload cap elsewhere. */
export const MAX_INTAKE_FILES = 20;

/** Document types that can propose a parcel account. Income documents (Rent Roll,
 *  Operating Statement) describe a property's economics and carry no account identity,
 *  so they cannot open an account and are refused by name rather than ignored. */
export const ACCOUNT_BEARING_TYPES = ['Notice of Value', 'Tax Bill'] as const;

export interface ParcelProposal {
  /** The uploaded document this proposal was read from. */
  documentId: string;
  filename: string;
  phase: 'uploading' | 'reading' | 'ready' | 'failed';
  accountNumber: string;
  ownerName: string;
  legalDescription: string;
  /** What the reader decided the document was. */
  docType: string | null;
  /** Lowest field confidence in the reading — the figure a reviewer should weigh. */
  lowestConfidence: number | null;
  error: string | null;
  /** Default FALSE. Nothing is created until a person ticks this. */
  confirmed: boolean;
}

interface ExtractResponse {
  status?: string;
  extracted_type?: string | null;
  raw_extraction_json?: string | null;
  error?: string | null;
  queued?: boolean;
}

/** A field envelope as the extraction schemas emit it. */
interface FieldEnvelope { value: unknown; confidence?: number }

function readField(fields: Record<string, FieldEnvelope> | undefined, key: string): string {
  const raw = fields?.[key]?.value;
  return raw === null || raw === undefined ? '' : String(raw);
}

/** Base64 for the upload endpoint, which takes inline bytes. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read this file from disk.'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export function ParcelDocumentIntake({
  propertyId,
  propertyState,
  proposals,
  onChange,
  disabled,
}: {
  propertyId: string;
  /** Narrows the duplicate/near-account check to the right state. */
  propertyState?: string | null;
  proposals: ParcelProposal[];
  /**
   * An UPDATER, never a snapshot. Reads take seconds and the confirm checkboxes stay
   * live throughout, so writing a pre-batch array back would revert whatever the
   * operator did meanwhile — including an un-tick, which would then create an account
   * they had explicitly refused. Every write below goes through the previous state.
   */
  onChange: (update: (prev: ParcelProposal[]) => ParcelProposal[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [overCap, setOverCap] = useState(0);

  /** Patch one proposal by documentId against the CURRENT state. */
  const patch = (documentId: string, p: Partial<ParcelProposal>) =>
    onChange((prev) => prev.map((x) => (x.documentId === documentId ? { ...x, ...p } : x)));
  const append = (row: ParcelProposal) => onChange((prev) => [...prev, row]);

  /**
   * Wait out a queued read.
   *
   * When a queue backend is configured the read POST returns 202 and does the work in a
   * worker — which is the NORMAL case in any deployed environment, since REDIS_URL is
   * set there. Treating that as a failure would leave document mode unable to create a
   * single account in production while working perfectly on a laptop. So poll the
   * document's reading history until one reaches a terminal state.
   */
  async function awaitQueuedRead(documentId: string): Promise<ExtractResponse | null> {
    const DEADLINE_MS = 120_000;
    const INTERVAL_MS = 2_000;
    const startedAt = Date.now();
    while (Date.now() - startedAt < DEADLINE_MS) {
      await new Promise((r) => setTimeout(r, INTERVAL_MS));
      try {
        const history = await api.get<{ extractions: ExtractResponse[] }>(
          `/api/document-extract/document/${documentId}`,
        );
        const latest = (history.extractions || [])[0];
        if (latest && latest.status && latest.status !== 'running') return latest;
      } catch {
        // A transient poll failure is not a read failure — keep waiting for the deadline.
      }
    }
    return null;
  }

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    const room = MAX_INTAKE_FILES - proposals.length;
    const picked = Array.from(files).slice(0, Math.max(0, room));
    setOverCap(files.length - picked.length);
    if (!picked.length) return;

    setBusy(true);
    try {
      for (const file of picked) {
        let documentId = '';
        try {
          const base64 = await toBase64(file);
          // Attach to the PROPERTY on upload. The parcel does not exist yet, and a
          // document that lands nowhere is a document nobody finds again; the parcel
          // link is added after the account is created.
          const uploaded = await api.post<{ id: string }>('/api/documents/upload', {
            filename: file.name,
            content_type: file.type || 'application/pdf',
            name: file.name,
            property_id: propertyId || null,
          file_data: base64,
          });
          documentId = uploaded.id;
          append({
            documentId,
            filename: file.name,
            phase: 'reading',
            accountNumber: '',
            ownerName: '',
            legalDescription: '',
            docType: null,
            lowestConfidence: null,
            error: null,
            confirmed: false,
          });

          const posted = await api.post<ExtractResponse>(
            `/api/document-extract/document/${documentId}`, {},
          );

          // 202 + queued means a worker is doing it — wait for it rather than calling
          // the perfectly healthy read a failure.
          const read = posted.queued ? await awaitQueuedRead(documentId) : posted;

          if (!read) {
            patch(documentId, {
              phase: 'failed',
              error: 'This document is still being read. Come back to this property and read it again in a few minutes.',
            });
            continue;
          }
          if (read.status !== 'succeeded') {
            patch(documentId, {
              phase: 'failed',
              error: read.error || 'This document could not be read.',
            });
            continue;
          }

          // The guard this constant always claimed to be. An income document describes a
          // property's economics and names no account, so it cannot open one; saying so
          // by name beats letting it through to fail as "no account number", which reads
          // like the reader was at fault.
          const readType = read.extracted_type || null;
          if (readType && !ACCOUNT_BEARING_TYPES.includes(readType as typeof ACCOUNT_BEARING_TYPES[number])) {
            patch(documentId, {
              phase: 'failed',
              docType: readType,
              error: `A ${readType} describes a property's income, not an account, so it cannot open one. Upload it from the property once the account exists.`,
            });
            continue;
          }

          const parsed = JSON.parse(read.raw_extraction_json || '{}') as {
            fields?: Record<string, FieldEnvelope>;
          };
          const fields = parsed.fields;
          const account = readField(fields, 'account_number').trim();
          const confidences = Object.values(fields || {})
            .map((f) => (typeof f?.confidence === 'number' ? f.confidence : null))
            .filter((c): c is number => c !== null);

          patch(documentId, {
            phase: account ? 'ready' : 'failed',
            accountNumber: account,
            ownerName: readField(fields, 'owner_name').trim(),
            legalDescription: readField(fields, 'legal_description').trim(),
            docType: read.extracted_type || null,
            lowestConfidence: confidences.length ? Math.min(...confidences) : null,
            error: account
              ? null
              : 'No account number could be read from this document, so it cannot propose an account.',
          });
        } catch (err) {
          const message = err instanceof ApiClientError ? err.message : 'This document could not be read.';
          if (documentId) {
            patch(documentId, { phase: 'failed', error: message });
          } else {
            append({
              documentId: `failed:${file.name}:${Date.now()}`,
              filename: file.name,
              phase: 'failed',
              accountNumber: '',
              ownerName: '',
              legalDescription: '',
              docType: null,
              lowestConfidence: null,
              error: message,
              confirmed: false,
            });
          }
        }
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const ready = proposals.filter((p) => p.phase === 'ready');
  const confirmedCount = proposals.filter((p) => p.confirmed && p.phase === 'ready').length;
  /**
   * Accounts proposed more than once IN THIS BATCH — almost always the same file picked
   * twice. The server-side duplicate check cannot see these: none of them exists yet, so
   * it compares against the database and finds nothing, and confirming both would create
   * two accounts for one parcel.
   */
  const repeatedAccounts = new Set(
    ready
      .map((p) => p.accountNumber)
      .filter((a, i, arr) => a && arr.indexOf(a) !== i),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div style={{
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        fontSize: 'var(--fs-sm)',
        color: 'var(--color-ink-2)',
      }}>
        Upload up to {MAX_INTAKE_FILES} value notices or tax bills. Each one proposes an
        account — <strong>nothing is created until you confirm it below</strong>. The
        property and assessor you already chose are what the documents can't tell us.
      </div>

      <div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*"
          disabled={disabled || busy || proposals.length >= MAX_INTAKE_FILES}
          onChange={(e) => handleFiles(e.target.files)}
          aria-label="Choose documents to read"
        />
        {busy && (
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            Reading… this takes a few seconds per document.
          </p>
        )}
        {overCap > 0 && (
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
            {overCap} file{overCap === 1 ? ' was' : 's were'} left out — {MAX_INTAKE_FILES} at a time.
          </p>
        )}
      </div>

      {proposals.length > 0 && (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
          {ready.length} account{ready.length === 1 ? '' : 's'} proposed ·{' '}
          <strong>{confirmedCount} confirmed for creation</strong>
        </p>
      )}

      {proposals.map((p) => (
        <div
          key={p.documentId}
          style={{
            border: `1px solid ${p.phase === 'failed' ? 'var(--color-status-red)' : 'var(--color-border)'}`,
            borderRadius: 'var(--r-lg)',
            padding: 'var(--sp-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
              {p.filename}
            </span>
            {p.docType && (
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{p.docType}</span>
            )}
          </div>

          {p.phase === 'reading' && (
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Reading…</span>
          )}

          {p.phase === 'failed' && (
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-status-red)' }}>{p.error}</span>
          )}

          {p.phase === 'ready' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
                <Readout label="Account" value={p.accountNumber} mono />
                <Readout label="Owner" value={p.ownerName || '—'} />
                <Readout label="Legal" value={p.legalDescription || '—'} />
              </div>
              {typeof p.lowestConfidence === 'number' && p.lowestConfidence < 0.9 && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
                  Lowest field confidence {Math.round(p.lowestConfidence * 100)}% — check the account number against the document.
                </span>
              )}
              {/* Exact duplicates AND leading-zero near matches. The near case is the
                  reason this is here at all: a county statement prints 00042951001 for
                  the parcel its appraisal district calls 42951001, so reading both
                  documents would otherwise open a second account for one parcel. */}
              <ParcelDuplicateWarning
                values={{ account_number: p.accountNumber, state: propertyState ?? '' } as FormValues}
              />
              {repeatedAccounts.has(p.accountNumber) && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
                  Another document in this batch proposes account {p.accountNumber} too — usually the
                  same file picked twice. Confirm only one, or you will open two accounts for one parcel.
                </span>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
                <input
                  type="checkbox"
                  checked={p.confirmed}
                  disabled={disabled}
                  onChange={() => patch(p.documentId, { confirmed: !p.confirmed })}
                />
                Create account <strong>{p.accountNumber}</strong>
              </label>
            </>
          )}

          {p.phase === 'failed' && (
            <div>
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => onChange((prev) => prev.filter((x) => x.documentId !== p.documentId))}
                style={{ color: 'var(--color-status-red)' }}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Readout({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ fontFamily: mono ? 'var(--font-mono)' : undefined, color: 'var(--color-ink)' }}>{value}</span>
    </span>
  );
}
